import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '@/components/ui/chart';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Cell } from 'recharts';
import { AgeDistribution } from '@/api/analytics';
import { useNavigate } from 'react-router-dom';

interface ActionAgeChartProps {
  data: AgeDistribution[];
  period: string;
  includeShared: boolean;
}

const chartConfig = {
  count: {
    label: 'Actions',
  },
};

// Define colors for each age range
const getColorForRange = (range: string) => {
  const colors: Record<string, string> = {
    '0-7 days': 'hsl(var(--chart-2))',     // Green
    '7-14 days': 'hsl(var(--chart-1))',    // Blue
    '14-30 days': 'hsl(var(--chart-4))',   // Purple
    '30-60 days': 'hsl(var(--chart-3))',   // Orange
    '60+ days': 'hsl(var(--chart-5))',     // Pink/Red
  };
  return colors[range] || 'hsl(var(--chart-1))';
};

export function ActionAgeChart({ data, period, includeShared }: ActionAgeChartProps) {
  const navigate = useNavigate();
  const totalActions = data.reduce((sum, item) => sum + item.count, 0);

  const handleBarClick = (data: AgeDistribution) => {
    if (data.count > 0) {
      navigate(`/app/actions?ageRange=${encodeURIComponent(data.range)}&period=${period}&includeShared=${includeShared}`);
    }
  };

  if (totalActions === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Action Age Distribution</CardTitle>
          <CardDescription>How long actions have been open</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground">
            No open actions
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Action Age Distribution</CardTitle>
        <CardDescription>How long actions have been open</CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} layout="vertical" margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis type="number" tick={{ fontSize: 12 }} />
              <YAxis 
                type="category" 
                dataKey="range" 
                tick={{ fontSize: 11 }}
                width={90}
              />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Bar 
                dataKey="count" 
                radius={[0, 4, 4, 0]} 
                maxBarSize={40}
                onClick={handleBarClick}
                className="cursor-pointer"
              >
                {data.map((entry) => (
                  <Cell 
                    key={`cell-${entry.range}`} 
                    fill={getColorForRange(entry.range)}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
